import { useState, type ReactNode } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import {
  AppBar,
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  Typography,
  useColorScheme,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonthOutlined';
import CampaignIcon from '@mui/icons-material/CampaignOutlined';
import ListAltIcon from '@mui/icons-material/ListAltOutlined';
import InsightsIcon from '@mui/icons-material/InsightsOutlined';
import GroupIcon from '@mui/icons-material/GroupOutlined';
import HistoryIcon from '@mui/icons-material/HistoryOutlined';
import AccountTreeIcon from '@mui/icons-material/AccountTreeOutlined';
import CategoryIcon from '@mui/icons-material/CategoryOutlined';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOverOutlined';
import ForumIcon from '@mui/icons-material/ForumOutlined';
import TopicIcon from '@mui/icons-material/TopicOutlined';
import AddIcon from '@mui/icons-material/Add';
import PersonIcon from '@mui/icons-material/PersonOutlined';
import SettingsIcon from '@mui/icons-material/SettingsOutlined';
import LogoutIcon from '@mui/icons-material/LogoutOutlined';
import { Permission, ROLE_LABELS } from '@pd/shared';
import { useAuth } from '../auth/AuthContext';
import { NotificationBell } from '../features/notifications/NotificationBell';
import { useSidebarCollapsed } from '../lib/sidebar';
import { t } from '../i18n/sq';
import { APPEARANCE } from './appearance';
import { BrandLogo } from './BrandLogo';
import { UserAvatar } from './UserAvatar';

const DRAWER_WIDTH = 260;
const COLLAPSED_WIDTH = 72;

interface NavItem {
  to: string;
  label: string;
  icon: ReactNode;
  permission?: Permission;
}

const NAV: NavItem[] = [
  {
    to: '/paneli',
    label: t.nav.dashboard,
    icon: <InsightsIcon />,
    permission: Permission.DASHBOARD_VIEW,
  },
  { to: '/kalendari', label: t.nav.calendar, icon: <CalendarMonthIcon /> },
  { to: '/aktivitetet', label: t.nav.activities, icon: <ListAltIcon /> },
  { to: '/mesazhet', label: t.nav.messages, icon: <RecordVoiceOverIcon /> },
  {
    to: '/shqetesimet',
    label: t.nav.concerns,
    icon: <ForumIcon />,
    permission: Permission.DASHBOARD_VIEW,
  },
  {
    to: '/lajmerimet',
    label: t.nav.announcements,
    icon: <CampaignIcon />,
    permission: Permission.ANNOUNCEMENT_SEND,
  },
  {
    to: '/perdoruesit',
    label: t.nav.users,
    icon: <GroupIcon />,
    permission: Permission.USER_MANAGE,
  },
  {
    to: '/struktura',
    label: t.nav.structure,
    icon: <AccountTreeIcon />,
    permission: Permission.REFERENCE_DATA_MANAGE,
  },
  {
    to: '/kategorite',
    label: t.nav.categories,
    icon: <CategoryIcon />,
    permission: Permission.REFERENCE_DATA_MANAGE,
  },
  {
    to: '/temat',
    label: t.nav.topics,
    icon: <TopicIcon />,
    permission: Permission.REFERENCE_DATA_MANAGE,
  },
  { to: '/auditimi', label: t.nav.audit, icon: <HistoryIcon />, permission: Permission.AUDIT_VIEW },
];

export function AppLayout() {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const { collapsed: collapsedPref, toggle: toggleCollapsed } = useSidebarCollapsed();
  const { user, can, logout } = useAuth();
  const { mode, setMode } = useColorScheme();
  const navigate = useNavigate();
  const location = useLocation();

  const closeMenu = () => setMenuAnchor(null);
  const go = (to: string) => {
    closeMenu();
    navigate(to);
  };

  // The forced first-login password change is shown full-screen, without navigation.
  if (user?.mustChangePassword) return <Outlet />;

  // Collapsing only exists on desktop; the phone menu is a full drawer.
  const collapsed = isDesktop && collapsedPref;
  const width = collapsed ? COLLAPSED_WIDTH : DRAWER_WIDTH;
  const widthTransition = theme.transitions.create(['width', 'margin'], {
    duration: theme.transitions.duration.shorter,
  });

  /** In the collapsed strip the label moves into a tooltip; screen readers keep the name. */
  const withTooltip = (label: string, node: ReactNode) =>
    collapsed ? (
      <Tooltip title={label} placement="right">
        <span style={{ display: 'block' }}>{node}</span>
      </Tooltip>
    ) : (
      node
    );

  const drawer = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflowX: 'hidden' }}>
      <Box
        sx={{ px: collapsed ? 1 : 2.5, pt: 2.5, pb: 2, textAlign: collapsed ? 'center' : 'left' }}
      >
        {collapsed ? (
          <BrandLogo emblem sx={{ height: 40, justifyContent: 'center' }} />
        ) : (
          <>
            <BrandLogo sx={{ height: 44 }} />
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ display: 'block', mt: 1, lineHeight: 1.4, letterSpacing: '0.08em' }}
            >
              {t.appTagline}
            </Typography>
          </>
        )}
      </Box>
      <Divider />
      <List sx={{ px: collapsed ? 1 : 1.5, py: 2, flex: 1 }}>
        {NAV.filter((n) => !n.permission || can(n.permission)).map((n) => {
          const active = location.pathname.startsWith(n.to);
          return (
            <ListItem key={n.to} disablePadding sx={{ mb: 0.5, display: 'block' }}>
              {withTooltip(
                n.label,
                <ListItemButton
                  component={NavLink}
                  to={n.to}
                  onClick={() => setMobileOpen(false)}
                  selected={active}
                  aria-current={active ? 'page' : undefined}
                  aria-label={collapsed ? n.label : undefined}
                  sx={{
                    borderRadius: 2,
                    minHeight: 48,
                    justifyContent: collapsed ? 'center' : 'flex-start',
                    px: collapsed ? 0 : 2,
                  }}
                >
                  <ListItemIcon sx={{ minWidth: collapsed ? 0 : 40, justifyContent: 'center' }}>
                    {n.icon}
                  </ListItemIcon>
                  {!collapsed && <ListItemText primary={n.label} />}
                </ListItemButton>,
              )}
            </ListItem>
          );
        })}
      </List>
      {can(Permission.ACTIVITY_CREATE) && (
        <Box sx={{ p: collapsed ? 1 : 2 }}>
          {withTooltip(
            t.activity.new,
            <ListItemButton
              component={NavLink}
              to="/aktivitetet/i-ri"
              onClick={() => setMobileOpen(false)}
              aria-label={collapsed ? t.activity.new : undefined}
              sx={{
                borderRadius: 2,
                minHeight: 48,
                justifyContent: collapsed ? 'center' : 'flex-start',
                px: collapsed ? 0 : 2,
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                '&:hover': { bgcolor: 'primary.dark' },
              }}
            >
              <ListItemIcon
                sx={{ minWidth: collapsed ? 0 : 40, color: 'inherit', justifyContent: 'center' }}
              >
                <AddIcon />
              </ListItemIcon>
              {!collapsed && <ListItemText primary={t.activity.new} />}
            </ListItemButton>,
          )}
        </Box>
      )}
    </Box>
  );

  const sidebarToggleLabel = collapsed ? t.nav.expandSidebar : t.nav.collapseSidebar;

  return (
    <Box sx={{ display: 'flex', minHeight: '100dvh' }}>
      <AppBar
        position="fixed"
        color="inherit"
        sx={{
          width: { md: `calc(100% - ${width}px)` },
          ml: { md: `${width}px` },
          transition: widthTransition,
          borderBottom: 1,
          borderColor: 'divider',
          boxShadow: 'none',
        }}
      >
        <Toolbar>
          {isDesktop ? (
            <Tooltip title={sidebarToggleLabel}>
              <IconButton
                edge="start"
                onClick={toggleCollapsed}
                aria-label={sidebarToggleLabel}
                aria-expanded={!collapsed}
                aria-controls="pd-sidebar"
              >
                {collapsed ? <MenuIcon /> : <MenuOpenIcon />}
              </IconButton>
            </Tooltip>
          ) : (
            <IconButton
              edge="start"
              onClick={() => setMobileOpen(true)}
              aria-label={t.nav.openMenu}
            >
              <MenuIcon />
            </IconButton>
          )}
          <Box sx={{ flex: 1 }} />
          <NotificationBell />
          <IconButton
            onClick={(e) => setMenuAnchor(e.currentTarget)}
            sx={{ ml: 1, p: 0.5 }}
            aria-label={t.nav.account}
            aria-haspopup="menu"
            aria-expanded={!!menuAnchor}
          >
            <UserAvatar name={user?.fullName ?? ''} avatarUrl={user?.avatarUrl} size={32} ring />
          </IconButton>
          <Menu
            anchorEl={menuAnchor}
            open={!!menuAnchor}
            onClose={closeMenu}
            variant="menu"
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            slotProps={{
              paper: { sx: { mt: 1, width: 288, maxWidth: 'calc(100vw - 32px)', borderRadius: 3 } },
              list: {
                // A grid so the three appearance choices sit side by side while staying direct
                // children of the list, which keeps arrow-key navigation working through them.
                sx: {
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  columnGap: 0.75,
                  px: 1,
                  '& > *': { gridColumn: '1 / -1' },
                  '& > .pd-appearance': { gridColumn: 'auto' },
                  '& > .MuiMenuItem-root': { borderRadius: 2 },
                },
              },
            }}
          >
            <Box sx={{ px: 1, pt: 1, pb: 1.5, display: 'flex', gap: 1.5, alignItems: 'center' }}>
              <UserAvatar
                name={user?.fullName ?? ''}
                avatarUrl={user?.avatarUrl}
                size={44}
                ring
                decorative
              />
              <Box sx={{ minWidth: 0 }}>
                <Typography noWrap sx={{ fontWeight: 600 }}>
                  {user?.fullName}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.35 }}>
                  {user && ROLE_LABELS[user.role]}
                </Typography>
              </Box>
            </Box>
            <Divider sx={{ mb: 1 }} />
            <MenuItem onClick={() => go('/profili')}>
              <ListItemIcon>
                <PersonIcon fontSize="small" />
              </ListItemIcon>
              {t.profile.menu}
            </MenuItem>
            <MenuItem onClick={() => go('/cilesimet')}>
              <ListItemIcon>
                <SettingsIcon fontSize="small" />
              </ListItemIcon>
              {t.settings.menu}
            </MenuItem>
            <Typography
              id="pd-appearance-label"
              variant="caption"
              color="text.secondary"
              sx={{ px: 2, pt: 1.5, pb: 0.75, fontWeight: 600 }}
            >
              {t.appearance.label}
            </Typography>
            {APPEARANCE.map((a) => {
              const checked = (mode ?? 'system') === a.mode;
              return (
                <MenuItem
                  key={a.mode}
                  className="pd-appearance"
                  role="menuitemradio"
                  aria-checked={checked}
                  aria-describedby="pd-appearance-label"
                  onClick={() => setMode(a.mode)}
                  sx={{
                    flexDirection: 'column',
                    gap: 0.5,
                    py: 1,
                    px: 0.5,
                    minHeight: 64,
                    border: 1,
                    borderColor: checked ? 'primary.main' : 'divider',
                    color: checked ? 'primary.main' : 'text.secondary',
                    typography: 'caption',
                    fontWeight: checked ? 600 : 500,
                    textAlign: 'center',
                    whiteSpace: 'normal',
                    lineHeight: 1.2,
                  }}
                >
                  {a.icon}
                  {a.label}
                </MenuItem>
              );
            })}
            <Divider sx={{ mt: 1.5, mb: 1 }} />
            <MenuItem
              onClick={async () => {
                closeMenu();
                await logout();
                navigate('/hyr');
              }}
              sx={(theme) => ({
                color: 'error.main',
                fontWeight: 600,
                mb: 0.5,
                '&:hover, &.Mui-focusVisible': {
                  bgcolor: `rgba(${theme.vars!.palette.error.mainChannel} / 0.08)`,
                },
              })}
            >
              <ListItemIcon sx={{ color: 'inherit' }}>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              {t.auth.logout}
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        id="pd-sidebar"
        aria-label={t.nav.main}
        sx={{ width: { md: width }, flexShrink: { md: 0 }, transition: widthTransition }}
      >
        {isDesktop ? (
          <Drawer
            variant="permanent"
            open
            sx={{
              '& .MuiDrawer-paper': { width, transition: widthTransition, overflowX: 'hidden' },
            }}
          >
            {drawer}
          </Drawer>
        ) : (
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={() => setMobileOpen(false)}
            ModalProps={{ keepMounted: true }}
            sx={{ '& .MuiDrawer-paper': { width: DRAWER_WIDTH } }}
          >
            {drawer}
          </Drawer>
        )}
      </Box>

      <Box
        component="main"
        sx={{ flex: 1, minWidth: 0, p: { xs: 2, sm: 3 }, pt: { xs: 10, sm: 11 } }}
      >
        <Outlet />
      </Box>
    </Box>
  );
}
